import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { US_STATES, looksLikeEmail, looksLikeZip, looksLikePhone } from "@/lib/address";

const FIELDS = [
  { key: "name", label: "Full name", placeholder: "e.g. Maria Smith", type: "text", auto: "name", span: "sm:col-span-3" },
  { key: "relationship", label: "Relationship to driver", placeholder: "e.g. Spouse, Parent", type: "text", auto: "off", span: "sm:col-span-3" },
  { key: "phone", label: "Phone number", placeholder: "e.g. (555) 123-4567", type: "tel", auto: "tel", span: "sm:col-span-3",
    check: looksLikePhone, warn: "This doesn't look like a 10-digit phone number." },
  { key: "email", label: "Email", placeholder: "e.g. maria@example.com", type: "email", auto: "email", span: "sm:col-span-3",
    check: looksLikeEmail, warn: "This doesn't look like a valid email, e.g. name@example.com." },
  { key: "street", label: "Home address", placeholder: "Street address", type: "text", auto: "street-address", span: "sm:col-span-6" },
  { key: "city", label: "City", placeholder: "City", type: "text", auto: "address-level2", span: "sm:col-span-2" },
  { key: "state", label: "State", select: true, span: "sm:col-span-2" },
  { key: "zip", label: "ZIP code", placeholder: "e.g. 75001", type: "text", auto: "postal-code", span: "sm:col-span-2",
    check: looksLikeZip, warn: "ZIP codes are 5 digits, e.g. 75001 or 75001-1234." },
];

function StateSelect({ value, onChange, i }) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger id={`ec-state-${i}`} data-testid={`input-ec-state-${i}`} className="rounded-sm bg-[#141414] border-[#262626] h-11">
        <SelectValue placeholder="Select state" />
      </SelectTrigger>
      <SelectContent className="max-h-72">
        {US_STATES.map((s) => <SelectItem key={s.value} value={s.value} data-testid={`ec-state-${i}-option-${s.value}`}>{s.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

export default function EmergencyContact({ d, i, onChange }) {
  const ec = d.emergency_contact || {};
  const [touched, setTouched] = useState({});
  const setField = (k, v) => onChange({ ...d, emergency_contact: { ...ec, [k]: v } });
  return (
    <div className="mt-5 pt-4 border-t border-[#262626]" data-testid={`emergency-contact-${i}`}>
      <Label className="text-sm font-semibold text-white mb-2 block">Emergency contact</Label>
      <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
        {FIELDS.map((f) => {
          const val = ec[f.key] || "";
          const bad = f.check && touched[f.key] && val.trim() && !f.check(val);
          return (
            <div key={f.key} className={f.span}>
              <Label htmlFor={`ec-${f.key}-${i}`} className="text-[11px] text-muted-foreground mb-1 block">{f.label}</Label>
              {f.select ? <StateSelect value={val} onChange={(v) => setField("state", v)} i={i} /> : (
                <Input id={`ec-${f.key}-${i}`} data-testid={`input-ec-${f.key}-${i}`} type={f.type} autoComplete={f.auto}
                  maxLength={f.key === "street" ? 120 : 80} value={val} placeholder={f.placeholder}
                  inputMode={f.type === "tel" ? "tel" : f.type === "email" ? "email" : f.key === "zip" ? "numeric" : undefined}
                  aria-invalid={bad || undefined} onBlur={() => setTouched((t) => ({ ...t, [f.key]: true }))}
                  onChange={(e) => setField(f.key, e.target.value)}
                  className={`rounded-sm bg-[#141414] h-11 ${bad ? "border-[#F59E0B]" : "border-[#262626]"}`} />
              )}
              {bad && (
                <p className="mt-1 text-[11px] text-[#F59E0B] inline-flex items-start gap-1" data-testid={`warn-ec-${f.key}-${i}`}>
                  <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {f.warn}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
